import { Schema } from "effect";

// These fields describe failed observations, including malformed source ids.
// They intentionally retain diagnostic strings and numbers without branding.

const BlobTooLargeErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(400), byteLength: Schema.Number, limit: Schema.Number }))();
type BlobTooLargeErrorProperties = typeof BlobTooLargeErrorFields["~type.make.in"];
export const BlobTooLargeError = /* @__PURE__ */ (() => {
  class BlobTooLargeError extends Schema.TaggedError<BlobTooLargeError>()("BlobTooLargeError", BlobTooLargeErrorFields) {
    constructor(byteLength: number, limit: number);
    constructor(properties: BlobTooLargeErrorProperties, options?: Schema.MakeOptions);
    constructor(byteLength: number | BlobTooLargeErrorProperties, limit?: number | Schema.MakeOptions) {
      super(typeof byteLength === "number" ? { byteLength, limit: limit as number, message: `Blob is ${byteLength} bytes; the limit is ${limit}.` } : byteLength, typeof limit === "object" ? limit : undefined);
      this.name = "BlobTooLargeError";
    }
    toJSON() { return { name: this.name, byteLength: this.byteLength, limit: this.limit }; }
  }
  return BlobTooLargeError;
})();
export type BlobTooLargeError = InstanceType<typeof BlobTooLargeError>;

const BlobDigestMismatchErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), expected: Schema.String, observed: Schema.String }))();
type BlobDigestMismatchErrorProperties = typeof BlobDigestMismatchErrorFields["~type.make.in"];
export const BlobDigestMismatchError = /* @__PURE__ */ (() => {
  class BlobDigestMismatchError extends Schema.TaggedError<BlobDigestMismatchError>()("BlobDigestMismatchError", BlobDigestMismatchErrorFields) {
    constructor(expected: string, observed: string);
    constructor(properties: BlobDigestMismatchErrorProperties, options?: Schema.MakeOptions);
    constructor(expected: string | BlobDigestMismatchErrorProperties, observed?: string | Schema.MakeOptions) {
      super(typeof expected === "string" ? { expected, observed: observed as string, message: `Expected blob digest ${expected}, but observed ${observed}.` } : expected, typeof observed === "object" ? observed : undefined);
      this.name = "BlobDigestMismatchError";
    }
    toJSON() { return { name: this.name, expected: this.expected, observed: this.observed }; }
  }
  return BlobDigestMismatchError;
})();
export type BlobDigestMismatchError = InstanceType<typeof BlobDigestMismatchError>;

const BlobCorruptionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), blobId: Schema.String }))();
type BlobCorruptionErrorProperties = typeof BlobCorruptionErrorFields["~type.make.in"];
export const BlobCorruptionError = /* @__PURE__ */ (() => {
  class BlobCorruptionError extends Schema.TaggedError<BlobCorruptionError>()("BlobCorruptionError", BlobCorruptionErrorFields) {
    constructor(blobId: string | BlobCorruptionErrorProperties, options?: Schema.MakeOptions) {
      super(typeof blobId === "string" ? { blobId, message: `Blob ${blobId} failed integrity verification and is quarantined.` } : blobId, options);
      this.name = "BlobCorruptionError";
    }
    toJSON() { return { name: this.name, blobId: this.blobId }; }
  }
  return BlobCorruptionError;
})();
export type BlobCorruptionError = InstanceType<typeof BlobCorruptionError>;

const TreeCorruptionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), treeId: Schema.String }))();
type TreeCorruptionErrorProperties = typeof TreeCorruptionErrorFields["~type.make.in"];
export const TreeCorruptionError = /* @__PURE__ */ (() => {
  class TreeCorruptionError extends Schema.TaggedError<TreeCorruptionError>()("TreeCorruptionError", TreeCorruptionErrorFields) {
    constructor(treeId: string, reason: string);
    constructor(properties: TreeCorruptionErrorProperties, options?: Schema.MakeOptions);
    constructor(treeId: string | TreeCorruptionErrorProperties, reason?: string | Schema.MakeOptions) {
      super(typeof treeId === "string" ? { treeId, message: `Tree ${treeId} is corrupt: ${reason}.` } : treeId, typeof reason === "object" ? reason : undefined);
      this.name = "TreeCorruptionError";
    }
    toJSON() { return { name: this.name, treeId: this.treeId }; }
  }
  return TreeCorruptionError;
})();
export type TreeCorruptionError = InstanceType<typeof TreeCorruptionError>;

const InconsistentCaptureErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), subject: Schema.String, expectedVersion: Schema.NullOr(Schema.Number), observedVersion: Schema.NullOr(Schema.Number) }))();
type InconsistentCaptureErrorProperties = typeof InconsistentCaptureErrorFields["~type.make.in"];
export const InconsistentCaptureError = /* @__PURE__ */ (() => {
  class InconsistentCaptureError extends Schema.TaggedError<InconsistentCaptureError>()("InconsistentCaptureError", InconsistentCaptureErrorFields) {
    constructor(subject: string, expectedVersion: number | null, observedVersion: number | null);
    constructor(properties: InconsistentCaptureErrorProperties, options?: Schema.MakeOptions);
    constructor(subject: string | InconsistentCaptureErrorProperties, expectedVersion?: number | null | Schema.MakeOptions, observedVersion?: number | null) {
      super(typeof subject === "string" ? { subject, expectedVersion: expectedVersion as number | null, observedVersion: observedVersion as number | null, message: `Station capture changed at ${subject}: expected version ${expectedVersion}, observed ${observedVersion}.` } : subject, typeof expectedVersion === "object" && expectedVersion !== null ? expectedVersion : undefined);
      this.name = "InconsistentCaptureError";
    }
    toJSON() { return { name: this.name, subject: this.subject, expectedVersion: this.expectedVersion, observedVersion: this.observedVersion }; }
  }
  return InconsistentCaptureError;
})();
export type InconsistentCaptureError = InstanceType<typeof InconsistentCaptureError>;

export const WorktreeQuotaName = /* @__PURE__ */ (() => Schema.Literals(["maxRetainedWorktrees", "maxWorkspaces", "maxFiles", "maxTreeBytes", "maxPaths", "maxDeclarativeBytes", "maxManifestBytes"]))();
export type WorktreeQuotaName = typeof WorktreeQuotaName.Type;

const WorktreeQuotaErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(400), quota: WorktreeQuotaName, observed: Schema.Number, limit: Schema.Number }))();
type WorktreeQuotaErrorProperties = typeof WorktreeQuotaErrorFields["~type.make.in"];
export const WorktreeQuotaError = /* @__PURE__ */ (() => {
  class WorktreeQuotaError extends Schema.TaggedError<WorktreeQuotaError>()("WorktreeQuotaError", WorktreeQuotaErrorFields) {
    constructor(quota: WorktreeQuotaName, observed: number, limit: number);
    constructor(properties: WorktreeQuotaErrorProperties, options?: Schema.MakeOptions);
    constructor(quota: WorktreeQuotaName | WorktreeQuotaErrorProperties, observed?: number | Schema.MakeOptions, limit?: number) {
      super(typeof quota === "string" ? { quota, observed: observed as number, limit: limit as number, message: `Worktree ${quota} quota exceeded: observed ${observed}, limit ${limit}.` } : quota, typeof observed === "object" ? observed : undefined);
      this.name = "WorktreeQuotaError";
    }
    toJSON() { return { name: this.name, quota: this.quota, observed: this.observed, limit: this.limit }; }
  }
  return WorktreeQuotaError;
})();
export type WorktreeQuotaError = InstanceType<typeof WorktreeQuotaError>;

export const StationSourceIntegrityErrorCode = /* @__PURE__ */ (() => Schema.Literals(["invalid-source", "missing-commit", "missing-tree", "commit-tree-mismatch", "moving-source"]))();
export type StationSourceIntegrityErrorCode = typeof StationSourceIntegrityErrorCode.Type;

const StationSourceIntegrityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: StationSourceIntegrityErrorCode }))();
type StationSourceIntegrityErrorProperties = typeof StationSourceIntegrityErrorFields["~type.make.in"];
export const StationSourceIntegrityError = /* @__PURE__ */ (() => {
  class StationSourceIntegrityError extends Schema.TaggedError<StationSourceIntegrityError>()("StationSourceIntegrityError", StationSourceIntegrityErrorFields) {
    constructor(code: StationSourceIntegrityErrorCode, message: string);
    constructor(properties: StationSourceIntegrityErrorProperties, options?: Schema.MakeOptions);
    constructor(code: StationSourceIntegrityErrorCode | StationSourceIntegrityErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: message as string } : code, typeof message === "object" ? message : undefined);
      this.name = "StationSourceIntegrityError";
    }
    toJSON() { return { name: this.name, code: this.code }; }
  }
  return StationSourceIntegrityError;
})();
export type StationSourceIntegrityError = InstanceType<typeof StationSourceIntegrityError>;

export const StationTestRunPlanningErrorCode = /* @__PURE__ */ (() => Schema.Literals(["station-not-found", "space-not-found", "test-file-not-found"]))();
export type StationTestRunPlanningErrorCode = typeof StationTestRunPlanningErrorCode.Type;

const StationTestRunPlanningErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(404), code: StationTestRunPlanningErrorCode }))();
type StationTestRunPlanningErrorProperties = typeof StationTestRunPlanningErrorFields["~type.make.in"];
export const StationTestRunPlanningError = /* @__PURE__ */ (() => {
  class StationTestRunPlanningError extends Schema.TaggedError<StationTestRunPlanningError>()("StationTestRunPlanningError", StationTestRunPlanningErrorFields) {
    constructor(code: StationTestRunPlanningErrorCode, message: string);
    constructor(properties: StationTestRunPlanningErrorProperties, options?: Schema.MakeOptions);
    constructor(code: StationTestRunPlanningErrorCode | StationTestRunPlanningErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: message as string } : code, typeof message === "object" ? message : undefined);
      this.name = "StationTestRunPlanningError";
    }
    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return StationTestRunPlanningError;
})();
export type StationTestRunPlanningError = InstanceType<typeof StationTestRunPlanningError>;

export const HostedTestArtifactErrorCode = /* @__PURE__ */ (() => Schema.Literals(["artifact-unavailable", "source-authority-mismatch"]))();
export type HostedTestArtifactErrorCode = typeof HostedTestArtifactErrorCode.Type;

const HOSTED_TEST_MESSAGES = Object.freeze({
  "artifact-unavailable": "Published hosted-test artifact is unavailable.",
  "source-authority-mismatch": "Hosted-test artifact does not match immutable source authority."
});
const HostedTestArtifactErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: HostedTestArtifactErrorCode }).check(Schema.makeFilter(value => value.message === HOSTED_TEST_MESSAGES[value.code])))();
type HostedTestArtifactErrorProperties = typeof HostedTestArtifactErrorFields["~type.make.in"];
export const HostedTestArtifactError = /* @__PURE__ */ (() => {
  class HostedTestArtifactError extends Schema.TaggedError<HostedTestArtifactError>()("HostedTestArtifactError", HostedTestArtifactErrorFields) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is HostedTestArtifactError =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(code: HostedTestArtifactErrorCode | HostedTestArtifactErrorProperties, options?: Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: HOSTED_TEST_MESSAGES[code] } : code, options);
      this.name = "HostedTestArtifactError";
    }
    toJSON() { return { name: this.name, code: this.code }; }
  }
  return HostedTestArtifactError;
})();
export type HostedTestArtifactError = InstanceType<typeof HostedTestArtifactError>;

export const ImmutableTreeExecutionErrorCode = /* @__PURE__ */ (() => Schema.Literals(["immutable_execution_invalid_source", "immutable_execution_tree_not_found", "immutable_execution_tree_mismatch", "immutable_execution_namespace_mismatch", "immutable_execution_workspace_not_found", "immutable_execution_resource_not_isolated"]))();
export type ImmutableTreeExecutionErrorCode = typeof ImmutableTreeExecutionErrorCode.Type;

export const ImmutableTreeExecutionResourceFailure = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ kind: Schema.Literal("worker-binding"), bindingName: Schema.String, target: Schema.String }),
  Schema.Struct({ kind: Schema.Literal("binding"), bindingName: Schema.String, bindingKind: Schema.String,
    reason: Schema.Literals(["unsupported-binding-kind", "invalid-binding-declaration", "config-evaluation-failed"]) })
]))();
export type ImmutableTreeExecutionResourceFailure = typeof ImmutableTreeExecutionResourceFailure.Type;
type ImmutableTreeExecutionOptions = {
  status: number; treeId?: string; workspaceId?: string; resource?: ImmutableTreeExecutionResourceFailure;
};
const ImmutableTreeExecutionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.Int.check(Schema.isBetween({ minimum: 100, maximum: 599 })), code: ImmutableTreeExecutionErrorCode, treeId: Schema.optional(Schema.String), workspaceId: Schema.optional(Schema.String), resource: Schema.optional(ImmutableTreeExecutionResourceFailure) }))();
type ImmutableTreeExecutionErrorProperties = typeof ImmutableTreeExecutionErrorFields["~type.make.in"];
export const ImmutableTreeExecutionError = /* @__PURE__ */ (() => {
  class ImmutableTreeExecutionError extends Schema.TaggedError<ImmutableTreeExecutionError>()("ImmutableTreeExecutionError", ImmutableTreeExecutionErrorFields) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is ImmutableTreeExecutionError =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(code: ImmutableTreeExecutionErrorCode, message: string, options: ImmutableTreeExecutionOptions);
    constructor(properties: ImmutableTreeExecutionErrorProperties, options?: Schema.MakeOptions);
    constructor(code: ImmutableTreeExecutionErrorCode | ImmutableTreeExecutionErrorProperties, message?: string | Schema.MakeOptions, options?: ImmutableTreeExecutionOptions) {
      super(typeof code === "string" ? { code, message: message as string, ...options as ImmutableTreeExecutionOptions } : code, typeof message === "object" ? message : undefined);
      this.name = "ImmutableTreeExecutionError";
    }
    toJSON() { return { name: this.name, code: this.code, status: this.status, treeId: this.treeId, workspaceId: this.workspaceId, resource: this.resource }; }
  }
  return ImmutableTreeExecutionError;
})();
export type ImmutableTreeExecutionError = InstanceType<typeof ImmutableTreeExecutionError>;
type ImmutableTreeExecutionDetails = Omit<ImmutableTreeExecutionOptions, "status">;
const immutableExecution = (status: 400 | 404 | 409) =>
  (code: ImmutableTreeExecutionErrorCode, message: string, details: ImmutableTreeExecutionDetails = {}) =>
    new ImmutableTreeExecutionError(code, message, { ...details, status });
export const immutableExecutionInvalid = immutableExecution(400);
export const immutableExecutionMissing = immutableExecution(404);
export const immutableExecutionConflict = immutableExecution(409);

export const SourceFailure = /* @__PURE__ */ (() => Schema.Union([
  BlobTooLargeError, BlobDigestMismatchError, BlobCorruptionError, TreeCorruptionError,
  InconsistentCaptureError, WorktreeQuotaError, StationSourceIntegrityError,
  StationTestRunPlanningError, HostedTestArtifactError, ImmutableTreeExecutionError
]))();
export type SourceFailure = typeof SourceFailure.Type;
